// src/pages/ProfileCreation/Company/ShareholderProfile.js
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AlertContext from "../../../../../Context/Alert/AlertContext";
import Dropdown from "../../../../../components/dropdown/dropDown";
import { BaseURL } from "../../../../../Files/BaseURL";

const ShareholderProfile = () => {
  const navigate = useNavigate();
  const { showAlert } = useContext(AlertContext);

  const [CountryList, setCountryList] = useState([]);
  const [shareholders, setShareholders] = useState([
    {
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      streetAddress: "",
      city: "",
      country: "",
      state: "",
      zip: "",
      owns: ""
    }
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch countries on component mount
  const fetchCountry = async () => {
    try {
      const response = await fetch(`${BaseURL}/api/auth/getcountry`);
      if (!response.ok) {
        throw new Error("Failed to fetch countries.");
      }
      const data = await response.json();
      setCountryList(data.Country);
    } catch (error) {
      showAlert(error.message, 'danger');
    }
  };

  useEffect(() => {
    fetchCountry();
  }, []);

  // Handle shareholder field changes
  const handleShareholderChange = (index, field, value) => {
    const updatedShareholders = [...shareholders];
    updatedShareholders[index][field] = value;
    setShareholders(updatedShareholders);
  };

  // Add a new shareholder
  const addShareholder = () => {
    setShareholders([
      ...shareholders,
      {
        firstName: "",
        lastName: "",
        dateOfBirth: "",
        streetAddress: "",
        city: "",
        country: "",
        state: "",
        zip: "",
        owns: ""
      }
    ]);
  };

  // Remove a shareholder
  const removeShareholder = (index) => {
    const updatedShareholders = shareholders.filter((_, i) => i !== index);
    setShareholders(updatedShareholders);
  };

  // Validate shareholders data
  const validateShareholders = () => {
    for (let i = 0; i < shareholders.length; i++) {
      const sh = shareholders[i];
      if (
        !sh.firstName.trim() ||
        !sh.lastName.trim() ||
        !sh.dateOfBirth ||
        !sh.streetAddress.trim() ||
        !sh.city.trim() ||
        !sh.country.trim() ||
        !sh.state.trim() ||
        !sh.zip.trim() ||
        !sh.owns.trim()
      ) {
        showAlert(`Please fill in all fields for shareholder ${i + 1}.`, 'danger');
        return false;
      }
      // Additional validations can be added here (e.g., date format, zip code length)
    }
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate data
    if (!validateShareholders()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const authToken = localStorage.getItem("p@token");
      const response = await fetch(`${BaseURL}/api/business/companysetup/shareHolder`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "auth-token": authToken,
        },
        body: JSON.stringify({
          ShareHolders: shareholders.map(sh => ({
            FirstName: sh.firstName,
            LastName: sh.lastName,
            DOB: sh.dateOfBirth,
            StreeAddress: sh.streetAddress,
            City: sh.city,
            Country: sh.country,
            ZipCode: sh.zip,
            Owning: sh.owns
          }))
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showAlert(data.message, "success");
        navigate("/profileCreation/company/structure");
      } else {
        const errorData = await response.json();
        showAlert(errorData.message || "Failed to save shareholders.", "danger");
      }
    } catch (error) {
      showAlert(error.message || "An error occurred.", "danger");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Progress Bar */}
      <div className="h-2 w-full bg-neutral-200 dark:bg-neutral-600">
        <div className="h-2 bg-green-500" style={{ width: "65%" }}></div>
      </div>

      {/* Form Container */}
      <div className="flex justify-center items-center w-[90%] my-10 md:w-[60%] xl:w-[30%] m-auto">
        <div className="card bg-white p-8 rounded-lg font-pop shadow-md w-full">
          <h2 className="text-2xl font-bold mb-4 text-center">Company Profile</h2>
          <p className="text-gray-600 mb-6">Shareholder Information</p>

          <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
            {shareholders.map((shareholder, index) => (
              <div key={index} className="border p-4 rounded-lg bg-gray-50 relative">
                {shareholders.length > 1 && (
                  <button
                    type="button"
                    className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                    onClick={() => removeShareholder(index)}
                    aria-label={`Remove shareholder ${index + 1}`}
                  >
                    &times;
                  </button>
                )}

                <h3 className="text-xl font-semibold mb-2">Shareholder {index + 1}</h3>

                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* First Name */}
                    <input
                      type="text"
                      placeholder="First Name"
                      className="w-full px-4 border border-gray-300 font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
                      value={shareholder.firstName}
                      onChange={(e) => handleShareholderChange(index, 'firstName', e.target.value)}
                      required
                    />

                    {/* Last Name */}
                    <input
                      type="text"
                      placeholder="Last Name"
                      className="w-full px-4 border border-gray-300 font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
                      value={shareholder.lastName}
                      onChange={(e) => handleShareholderChange(index, 'lastName', e.target.value)}
                      required
                    />
                  </div>

                  {/* Date of Birth */}
                  <input
                    type="date"
                    min="1900-01-01"
                    max={new Date().toISOString().split("T")[0]}
                    className="w-full px-4 border border-gray-300 font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
                    value={shareholder.dateOfBirth}
                    onChange={(e) => handleShareholderChange(index, 'dateOfBirth', e.target.value)}
                    required
                  />

                  {/* Street Address */}
                  <input
                    type="text"
                    placeholder="Street Address"
                    className="w-full px-4 border border-gray-300 font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
                    value={shareholder.streetAddress}
                    onChange={(e) => handleShareholderChange(index, 'streetAddress', e.target.value)}
                    required
                  />
                  {/* City */}
                  <input
                    type="text"
                    placeholder="City"
                    className="w-full px-4 border border-gray-300 font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
                    value={shareholder.city}
                    onChange={(e) => handleShareholderChange(index, 'city', e.target.value)}
                    required
                  />


                  {/* Country */}
                  <select
                    name="country"
                    id={`country-${index}`}
                    value={shareholder.country}
                    onChange={(e) => handleShareholderChange(index, 'country', e.target.value)}
                    className="w-full px-4 border border-gray-300 font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
                    required
                  >
                    <option value="">Select Country</option>
                    {CountryList.map((item, idx) => (
                      <option key={idx} value={item}>{item}</option>
                    ))}
                  </select>

                  {/* State */}
                  <input
                    type="text"
                    placeholder="State"
                    className="w-full px-4 border border-gray-300 font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
                    value={shareholder.state}
                    onChange={(e) => handleShareholderChange(index, 'state', e.target.value)}
                    required
                  />

                  {/* Zip Code */}
                  <input
                    type="text"
                    placeholder="Zip Code"
                    className="w-full px-4 border border-gray-300 font-pop text-lg rounded-full bg-[#E8E8E8] py-2 focus:border-primarygreen focus:outline-none"
                    value={shareholder.zip}
                    onChange={(e) => handleShareholderChange(index, 'zip', e.target.value)}
                    required
                  />

                  {/* Ownership Dropdown */}
                  <Dropdown
                    value={shareholder.owns}
                    onChange={(value) => handleShareholderChange(index, 'owns', value)}
                    placeholder="Ownership Percentage"
                  />
                </div>
              </div>
            ))}

            {/* Add Shareholder Button */}
            <div
              className="flex gap-2 items-center w-full text-primarygreen cursor-pointer"
              onClick={addShareholder}
            >
              <img src="../../Assets/Prop/Add.png" alt="Add" className='w-6 h-6' />
              <h2>Add another shareholder</h2>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`my-4 bg-green-500 ${isSubmitting ? "opacity-50 cursor-not-allowed" : "hover:bg-transparent hover:bg-white-500"
                } font-semibold hover:text-black py-2 px-4 border border-black-500 hover:border-green-500 rounded-full w-full text-white animate__animated animate__backInUp transition duration-300`}
            >
              {isSubmitting ? "Submitting..." : "Next"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default ShareholderProfile;
